import { Alert, Badge, Button, Form, Spinner, Table } from "react-bootstrap";
import type { IDwtChangedField, IWasteMovementDwtCorrection } from "@/customTypings/wasteMovement/WasteMovement";
import { dwtFilingName, dwtReasonLength, dwtReasonProblem } from "../core/dwtSubmissionDisplay";
import type { WasteMovementDwtCorrectionState } from "../react/useWasteMovementDwtCorrection";

export interface DwtCorrectionSummaryProps {
    correction: WasteMovementDwtCorrectionState | null;
    /** The user may change waste movements (the server checks again). */
    canCorrect: boolean;
    correcting: boolean;
    busy: boolean;
    onCorrect?: () => void;
}

/**
 * S2D: when the saved movement no longer matches what DEFRA holds for its accepted filing - "DEFRA filing requires
 * correction", what changed, and the "Correct DEFRA filing" action when the server says a correction can be sent. When the
 * saved movement is not ready, it says the edited movement cannot yet be sent as a correction - never that the existing
 * filing is invalid: that filing still stands.
 */
export function DwtCorrectionSummary({ correction, canCorrect, correcting, busy, onCorrect }: DwtCorrectionSummaryProps) {
    if (correction?.status === "error") {
        return (
            <div className="text-muted small mb-2" data-testid="dwt-correction-error">
                Whether the DEFRA filing needs a correction could not be checked. Reload the page to try again.
            </div>
        );
    }
    const loaded = correction?.status === "loaded" ? correction.correction : null;
    if (!loaded || !loaded.hasFiling || !loaded.requiresCorrection) return null;

    const filing = dwtFilingName(loaded.mode);
    const blockers = loaded.readiness?.blockers ?? [];
    return (
        <Alert variant={loaded.mode === "live" ? "danger" : "warning"} className="py-2 mb-2" data-testid="dwt-correction-banner" data-mode={loaded.mode ?? ""}>
            <div className="fw-semibold">DEFRA filing requires correction</div>
            <div className="small">
                The saved movement no longer matches the {filing}
                {loaded.wasteTrackingId ? ` (waste tracking ID ${loaded.wasteTrackingId})` : ""}. That filing still stands until it is corrected.
            </div>
            {loaded.changes.length > 0 && <DwtChangedFields changes={loaded.changes} />}
            {!loaded.movementReady && (
                <div className="small mt-2" data-testid="dwt-correction-not-ready">
                    <div className="fw-semibold">Current edited movement cannot yet be submitted as a correction.</div>
                    {blockers.length > 0 && (
                        <ul className="mb-1">
                            {blockers.map((b, index) => (
                                <li key={`${b.code}-${index}`}>{b.message}</li>
                            ))}
                        </ul>
                    )}
                    <div>The existing DEFRA filing is unchanged and still stands.</div>
                </div>
            )}
            {loaded.movementReady && !loaded.available && loaded.message && (
                <div className="small mt-2" data-testid="dwt-correction-unavailable">
                    {loaded.message}
                </div>
            )}
            {loaded.available && canCorrect && onCorrect && (
                <Button
                    className="mt-2"
                    size="sm"
                    variant={loaded.mode === "live" ? "danger" : "warning"}
                    onClick={onCorrect}
                    disabled={busy}
                    data-testid="dwt-correct-button"
                >
                    {correcting ? (
                        <>
                            <Spinner as="span" animation="border" size="sm" className="me-2" aria-hidden="true" />
                            Correcting…
                        </>
                    ) : (
                        "Correct DEFRA filing"
                    )}
                </Button>
            )}
        </Alert>
    );
}

/** The changed fields: what DEFRA holds and what the correction would send; approval-worthy ones marked. */
export function DwtChangedFields({ changes }: { changes: IDwtChangedField[] }) {
    return (
        <Table size="sm" className="small mt-2 mb-1 bg-transparent" data-testid="dwt-correction-changes">
            <thead>
                <tr>
                    <th>Field</th>
                    <th>DEFRA holds</th>
                    <th>Correction sends</th>
                </tr>
            </thead>
            <tbody>
                {changes.map((c) => (
                    <tr key={c.field} data-field={c.field}>
                        <td>
                            {c.label}
                            {c.needsApproval && (
                                <Badge bg="secondary" className="ms-1" data-testid="dwt-correction-needs-approval">
                                    second approver
                                </Badge>
                            )}
                        </td>
                        <td>{c.before ?? <span className="text-muted">nothing</span>}</td>
                        <td>{c.after ?? <span className="text-muted">nothing (blanked)</span>}</td>
                    </tr>
                ))}
            </tbody>
        </Table>
    );
}

export interface DwtCorrectionFormProps {
    correction: IWasteMovementDwtCorrection;
    reason: string;
    onReasonChange: (reason: string) => void;
    approverId: string;
    onApproverChange: (identityId: string) => void;
    confirmed: boolean;
    onConfirmedChange: (confirmed: boolean) => void;
    /** Why the last attempt to correct was not sent (e.g. the approver was refused). */
    error?: string | null;
    disabled?: boolean;
}

/** Whether the correction form may be sent: a valid reason, an approver when one is required, and explicit confirmation. */
export function dwtCorrectionFormReady(correction: IWasteMovementDwtCorrection, reason: string, approverId: string, confirmed: boolean): boolean {
    return (
        correction.available &&
        dwtReasonProblem(reason, correction.reasonMinLength, correction.reasonMaxLength) === null &&
        (!correction.approvalRequired || approverId.length > 0) &&
        confirmed
    );
}

/**
 * S2D: the correction dialog - which filing it changes (LIVE unmistakable), what changes, the reason for the regulator, a
 * second approver only when the weight, EWC or disposal/recovery code changes, who it is recorded as, and an explicit
 * confirmation. The server decides and checks everything again; the browser sends only the reason and the approver's id.
 */
export function DwtCorrectionForm({
    correction,
    reason,
    onReasonChange,
    approverId,
    onApproverChange,
    confirmed,
    onConfirmedChange,
    error,
    disabled = false,
}: DwtCorrectionFormProps) {
    const filing = dwtFilingName(correction.mode);
    const length = dwtReasonLength(reason);
    const reasonProblem = reason.length > 0 ? dwtReasonProblem(reason, correction.reasonMinLength, correction.reasonMaxLength) : null;
    return (
        <div data-testid="dwt-correction-form" data-mode={correction.mode ?? ""}>
            {correction.mode === "live" ? (
                <Alert variant="danger" className="mb-3" data-testid="dwt-correction-live">
                    <div className="fw-bold fs-5">LIVE - this changes the real DEFRA filing</div>
                    <div>DEFRA replaces the details it holds for this site's permit with the saved movement. The reason is kept with the filing and shown to the regulator.</div>
                </Alert>
            ) : (
                <Alert variant="warning" className="mb-3" data-testid="dwt-correction-test">
                    <div className="fw-semibold">This changes the existing {filing}</div>
                    <div>DEFRA's test service, not a real filing. The whole saved movement replaces the details it holds.</div>
                </Alert>
            )}
            <div className="small mb-2">
                DWT reference <span className="font-monospace">{correction.reference}</span>; waste tracking ID{" "}
                <span className="font-monospace">{correction.wasteTrackingId}</span> (it stays the same).
            </div>
            <DwtChangedFields changes={correction.changes} />
            <Form.Group className="mb-3" controlId="dwt-correction-reason">
                <Form.Label className="fw-semibold">Reason for the correction</Form.Label>
                <Form.Control
                    as="textarea"
                    rows={3}
                    value={reason}
                    maxLength={correction.reasonMaxLength * 2}
                    onChange={(e) => onReasonChange(e.target.value)}
                    disabled={disabled}
                    isInvalid={reasonProblem !== null}
                    data-testid="dwt-correction-reason"
                />
                <Form.Text muted>
                    {correction.reasonMinLength}-{correction.reasonMaxLength} characters ({length} so far). Shown to the regulator, so say what was wrong
                    and why it changed.
                </Form.Text>
                {reasonProblem && <Form.Control.Feedback type="invalid">{reasonProblem}</Form.Control.Feedback>}
            </Form.Group>
            {correction.approvalRequired && (
                <Form.Group className="mb-3" controlId="dwt-correction-approver">
                    <Form.Label className="fw-semibold">Approved by</Form.Label>
                    <Form.Select value={approverId} onChange={(e) => onApproverChange(e.target.value)} disabled={disabled} data-testid="dwt-correction-approver">
                        <option value="">Choose who approved this correction…</option>
                        {correction.approvers.map((a) => (
                            <option key={a.identityId} value={a.identityId}>
                                {a.email ? `${a.name} (${a.email})` : a.name}
                            </option>
                        ))}
                    </Form.Select>
                    <Form.Text muted>
                        This correction {correction.approvalReason ?? "changes the weight, EWC or disposal/recovery code"}, so a second person must approve it. They
                        are recorded as approving it.
                    </Form.Text>
                </Form.Group>
            )}
            {correction.amendedBy && (
                <div className="small text-muted mb-2" data-testid="dwt-correction-amended-by">
                    Recorded as corrected by {correction.amendedBy}.
                </div>
            )}
            <Form.Check
                id="dwt-correction-confirm"
                type="checkbox"
                checked={confirmed}
                onChange={(e) => onConfirmedChange(e.target.checked)}
                disabled={disabled}
                label={`I confirm this changes the existing ${correction.mode === "live" ? "LIVE DEFRA filing" : filing}.`}
                data-testid="dwt-correction-confirm-check"
            />
            {error && (
                <Alert variant="warning" className="py-2 mt-3 mb-0" data-testid="dwt-correction-form-error">
                    {error}
                </Alert>
            )}
        </div>
    );
}
